import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { initAnalytics, trackPageView, trackAddToCart } from './lib/analytics';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { Toaster } from './components/ui/sonner';
import { Header } from './components/Header';
import { HeroSlideshow } from './components/HeroSlideshow';
import { NewArrivalsSection, BestSellersSection } from './components/HomepageSections';
import { Categories } from './components/Categories';
import { BrandStory } from './components/BrandStory';
import { Testimonials } from './components/Testimonials';
import { Footer } from './components/Footer';
import { CartSheet } from './components/CartSheet';
import { WishlistSheet } from './components/WishlistSheet';
import { CouponBanner } from './components/CouponBanner';
import { ExitIntentPopup } from './components/ExitIntentPopup';
import { WelcomePopup } from './components/WelcomePopup';
import { FreedomSalePopup } from './components/FreedomSalePopup';
import { ReturnsExchange } from './pages/ReturnsExchange';
import { SizeGuide } from './pages/SizeGuide';
import { ContactUs } from './pages/ContactUs';
import { FAQs } from './pages/FAQs';
import { ShippingDelivery } from './pages/ShippingDelivery';
import { AboutUs } from './pages/AboutUs';
import { AdminLogin } from './pages/AdminLogin';
import { AdminDashboard } from './pages/AdminDashboard';

const AdminRedirect = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
  return <Navigate to={token ? '/admin/dashboard' : '/admin/login'} replace />;
};

// Tracks SPA route changes for analytics. Mounted inside <BrowserRouter>.
const RouteTracker = () => {
  const location = useLocation();
  const lastPath = React.useRef(null);
  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPath.current === path) return; // guard React StrictMode double-fire
    lastPath.current = path;
    trackPageView(path);
  }, [location.pathname, location.search]);
  return null;
};
import { CustomerLogin } from './pages/CustomerLogin';
import { CustomerAccount } from './pages/CustomerAccount';
import { Checkout } from './pages/Checkout';
import { SearchResults } from './pages/SearchResults';
import { WhatsAppButton } from './components/WhatsAppButton';

function App() {
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('cartItems');
    return saved ? JSON.parse(saved) : [];
  });
  const [wishlistItems, setWishlistItems] = useState(() => {
    const saved = localStorage.getItem('wishlistItems');
    return saved ? JSON.parse(saved) : [];
  });

  // Persist cart and wishlist to localStorage
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    localStorage.setItem('wishlistItems', JSON.stringify(wishlistItems));
  }, [wishlistItems]);

  // Initialise GA4, Meta Pixel, MS Clarity, and the internal tracker once
  useEffect(() => {
    initAnalytics();
  }, []);

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleAddToCart = (product) => {
    trackAddToCart(product, 1);
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const handleAddToWishlist = (product) => {
    setWishlistItems(prev => {
      if (prev.find(item => item.id === product.id)) {
        return prev;
      }
      return [...prev, product];
    });
  };

  const handleRemoveFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  };

  const handleRemoveFromWishlist = (productId) => {
    setWishlistItems(prev => prev.filter(item => item.id !== productId));
  };

  const handleUpdateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      handleRemoveFromCart(productId);
      return;
    }
    setCartItems(prev => 
      prev.map(item => 
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <BrowserRouter>
      <AuthProvider>
        <CustomerAuthProvider>
          <div className="App bg-[#FAF9F7] min-h-screen">
            <RouteTracker />
            {/* Routes */}
            <Routes>
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminRedirect />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />

              {/* Customer Auth Routes */}
              <Route path="/login" element={<CustomerLogin />} />

              {/* Public Routes with Header and Footer */}
              <Route path="/*" element={
                <>
                  <Header
                    onCartClick={() => setCartOpen(true)}
                    onWishlistClick={() => setWishlistOpen(true)}
                    cartCount={getCartCount()}
                  />
                  <Routes>
                    {/* Home Page */}
                    <Route path="/" element={
                      <main>
                        <HeroSlideshow onAddToCart={handleAddToCart} onAddToWishlist={handleAddToWishlist} />
                        <CouponBanner variant="home" />
                        <NewArrivalsSection
                          onAddToCart={handleAddToCart}
                          onAddToWishlist={handleAddToWishlist}
                        />
                        <BestSellersSection
                          onAddToCart={handleAddToCart}
                          onAddToWishlist={handleAddToWishlist}
                        />
                        <Categories />
                        <BrandStory />
                        <Testimonials />
                      </main>
                    } />

                    {/* Search & Products */}
                    <Route path="/search" element={
                      <SearchResults 
                        onAddToCart={handleAddToCart}
                        onAddToWishlist={handleAddToWishlist}
                      />
                    } />

                    {/* Checkout */}
                    <Route path="/checkout" element={
                      <Checkout 
                        cartItems={cartItems}
                        getCartTotal={getCartTotal}
                        clearCart={clearCart}
                      />
                    } />

                    {/* Customer Account */}
                    <Route path="/account" element={<CustomerAccount />} />
                    <Route path="/account/orders" element={<CustomerAccount />} />

                    {/* Policy Pages */}
                    <Route path="/returns-exchange" element={<ReturnsExchange />} />
                    <Route path="/size-guide" element={<SizeGuide />} />
                    <Route path="/contact" element={<ContactUs />} />
                    <Route path="/faqs" element={<FAQs />} />
                    <Route path="/shipping-delivery" element={<ShippingDelivery />} />
                    <Route path="/about" element={<AboutUs />} />
                  </Routes>

                  {/* Footer */}
                  <Footer />

                  {/* Cart Sheet */}
                  <CartSheet
                    isOpen={cartOpen}
                    onClose={() => setCartOpen(false)}
                    cartItems={cartItems}
                    onUpdateQuantity={handleUpdateCartQuantity}
                    onRemoveItem={handleRemoveFromCart}
                    cartTotal={getCartTotal()}
                    onCheckout={() => {
                      setCartOpen(false);
                    }}
                  />

                  {/* Wishlist Sheet */}
                  <WishlistSheet
                    isOpen={wishlistOpen}
                    onClose={() => setWishlistOpen(false)}
                    wishlistItems={wishlistItems}
                    onAddToCart={handleAddToCart}
                    onRemoveFromWishlist={handleRemoveFromWishlist}
                  />

                  {/* Toast Notifications */}
                  <Toaster position="bottom-right" richColors />
                  
                  {/* WhatsApp Floating Button */}
                  <WhatsAppButton variant="floating" />

                  {/* Exit-intent popup (desktop only, fires once per session) */}
                  <ExitIntentPopup cartItems={cartItems} />

                  {/* Welcome popup on landing page (once per session, ~2.5s after load) */}
                  <WelcomePopup />

                  {/* Freedom Sale popup — only during 09–19 Aug 2026 IST campaign window */}
                  <FreedomSalePopup />
                </>
              } />
            </Routes>
          </div>
        </CustomerAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
